/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useQuery} from '@tanstack/react-query'
import {
    useAccessToken,
    useCategory,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../../utils/site-config'

// Chakra
import {
    Box,
    useDisclosure,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Hooks
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {LOGIN_VIEW, REGISTER_VIEW, useAuthModal} from '../../hooks/use-auth-modal'
import {useClaraChat} from '../../hooks/use-clara-chat'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {hasOptimizelyContent} from '../../utils/optimizely-content'
import {useAccountModal} from '../account-modal/useAccountModal'
import {AccountModalProvider} from '../account-modal/useAccountModal'
import {useNavigationHandlers} from './hooks/use-navigation-handlers'
import {useAppEffects} from './hooks/use-app-effects'
import {useDNT} from '@salesforce/commerce-sdk-react'
// Others
import {flatten, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {CAT_MENU_DEFAULT_NAV_SSR_DEPTH, DEFAULT_LOCALE} from '../../constants'

import {getRootCategoryId} from '../../utils/brandUtils'
import {useBrand} from '../../contexts/brand-context'
import {useActiveCategories, filterActiveChildren} from '../../hooks/use-active-categories'

// Localization
import {IntlProvider} from 'react-intl'

// Components
import {Helmet} from 'react-helmet'
import Optimizely from '../optimizely'
import ContentFallback from '../optimizely/utils/contentFallback'
import PreviewModeProjectSelector from '../preview-mode-project-selector'
import Script from '../script'
import {GTMScript} from '../../utils/gtm-script'
import {toSHA256} from '../../utils/gtm-utils'
import {DidomiSDK} from './lazy-components'
import {BrazeScript} from '../../utils/braze-script'
import {LogRocketScript} from '../../utils/logrocket-script'

// Partials
import AppProviders from './partials/AppProviders'
import MainLayout from './partials/MainLayout'
import PendingWishlistReplay from './partials/PendingWishlistReplay'

const App = (props) => {
    const {children} = props
    const {site, locale, buildUrl} = useMultiSite()
    const {brandId, isGarage} = useBrand()
    const {app: appConfig} = getConfig()
    const siteId = site?.id
    const {selectedDnt, updateDNT} = useDNT()
    // didomi variables
    const DIDOMI_PUBLIC_API_KEY = getSiteEnvVarValue(appConfig, 'DIDOMI_PUBLIC_API_KEY', siteId)
    const DIDOMI_NOTICE_ID_GARAGE = getSiteEnvVarValue(appConfig, 'DIDOMI_NOTICE_ID_GARAGE', siteId)
    const DIDOMI_NOTICE_ID_DYNAMITE = getSiteEnvVarValue(appConfig, 'DIDOMI_NOTICE_ID_DYNAMITE', siteId)
    const IS_DIDOMI_ENABLED_GARAGE = getSiteEnvVarValue(appConfig, 'IS_DIDOMI_ENABLED_GARAGE', siteId) === 'true'
    const IS_DIDOMI_ENABLED_DYNAMITE = getSiteEnvVarValue(appConfig, 'IS_DIDOMI_ENABLED_DYNAMITE', siteId) === 'true'
    const IS_DIDOMI_ENABLED = isGarage ? IS_DIDOMI_ENABLED_GARAGE : IS_DIDOMI_ENABLED_DYNAMITE
    const DIDOMI_NOTICE_ID = isGarage ? DIDOMI_NOTICE_ID_GARAGE : DIDOMI_NOTICE_ID_DYNAMITE
    // Force the consent banner to the site locale instead of the visitor's browser language.
    // Didomi uses 2-letter codes; a single enabled language + matching default pins it (no separate force flag).
    const DIDOMI_LANGUAGE = (locale?.id || '').split(/[-_]/)[0] || 'en'
    // Maintenance mode check - must be at the top to disable unnecessary API calls
    const isMaintenanceEnabled =
        getSiteEnvVarValue(appConfig, 'MAINTENANCE_GLOBAL_WEB_ENABLED', siteId) === 'true' &&
        getSiteEnvVarValue(appConfig, 'MAINTENANCE_GLOBAL_WEB_PAGE_ID', siteId) &&
        getSiteEnvVarValue(appConfig, 'MAINTENANCE_GLOBAL_WEB_PAGE_ID', siteId) !== ''

    const [isDidomiReady, setIsDidomiReady] = useState(!IS_DIDOMI_ENABLED)
    const normalizedLang = locale?.id?.replace('_', '-')

    /*-------- Country Selector --------*/
    const [showCountrySelectorOnFooter, setShowCountrySelectorOnFooter] = useState()
    const handleShowCountrySelectorOnFooter = (showCSOnFooter) => {
        setShowCountrySelectorOnFooter(showCSOnFooter)
    }

    /*-------- Category data --------*/
    const ROOT_CATEGORY = getRootCategoryId(site.id, brandId)
    const {data: categoriesTree} = useCategory({
        parameters: {id: ROOT_CATEGORY, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH},
        enabled: !isMaintenanceEnabled
    })
    // Only surface categories that have online/orderable products assigned. The
    // active-categories lookup runs asynchronously (non-blocking); the nav renders
    // immediately and filters once the (cached) result is available.
    const {activeCategoryIds} = useActiveCategories(ROOT_CATEGORY, {
        enabled: !isMaintenanceEnabled
    })
    const categories = useMemo(() => {
        const flat = flatten(categoriesTree || {}, 'categories')
        const root = flat?.[ROOT_CATEGORY]
        if (root) {
            flat[ROOT_CATEGORY] = filterActiveChildren(root, activeCategoryIds, 'categories')
        }
        return flat
    }, [categoriesTree, activeCategoryIds, ROOT_CATEGORY])

    /*-------- Core hooks --------*/
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const activeData = useActiveData()
    const location = useLocation()
    const authModal = useAuthModal()
    const accountModalProps = useAccountModal()

    const [isSignifydReady, setSignifydReady] = useState(false)
    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')
    const {isOpen, onOpen, onClose} = useDisclosure()

    useEffect(() => {
        if (typeof document === 'undefined' || typeof window === 'undefined') return undefined

        const handleClick = (event) => {
            const drawerLink = event.target?.closest?.('[data-gdi-drawer]')
            if (!drawerLink) return

            event.preventDefault()
            window.dispatchEvent(
                new CustomEvent('gdi:openDrawer', {
                    detail: {drawer: drawerLink.getAttribute('data-gdi-drawer')}
                })
            )
        }

        document.addEventListener('click', handleClick, true)
        return () => document.removeEventListener('click', handleClick, true)
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined') return undefined

        const handleOpenDrawer = (event) => {
            const drawer = event?.detail?.drawer

            if (drawer === 'login' || drawer === 'register') {
                authModal.setInitialView(drawer === 'register' ? REGISTER_VIEW : LOGIN_VIEW)
                authModal.onOpen()
                return
            }

            if (drawer === 'account') {
                accountModalProps.onOpen()
                return
            }

            // Cookie preferences: not an in-app drawer, opens the Didomi consent
            // preferences UI directly. Shared by both the CMS `{link: {linkType:
            // 'drawer', drawerId: 'didomi'}}` directive and GDILinkBlock's
            // "System Drawer ID" LinkType, since both dispatch this same event.
            if (drawer === 'didomi') {
                if (typeof window !== 'undefined' && window.Didomi) {
                    window.Didomi.preferences.show()
                }
            }
        }

        window.addEventListener('gdi:openDrawer', handleOpenDrawer)
        return () => window.removeEventListener('gdi:openDrawer', handleOpenDrawer)
    }, [accountModalProps.onOpen, authModal.onOpen, authModal.setInitialView])

    /*-------- Locale & translations --------*/
    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                return {}
            }
            return fetchTranslations(targetLocale, appOrigin)
        },
        enabled: isServer
    })

    /*-------- Route conditionals --------*/
    // Used to conditionally render header/footer for checkout page (but not confirmation page)
    const isCart = /\/cart(\/|$)/.test(location?.pathname)
    const isCheckout = /\/checkout$/.test(location?.pathname)
    const isCheckoutConfirmation = /\/checkout\/confirmation/.test(location?.pathname)
    const isSsoCallback = /\/sso-callback\/?$/.test(location?.pathname)
    useClaraChat({enabled: !isCheckout && !isSsoCallback && !isMaintenanceEnabled})

    /*-------- Currency & customer/basket data --------*/
    const {l10n} = site
    const currency = locale.preferredCurrency || l10n.defaultCurrency
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    const [brazeUserId, setBrazeUserId] = useState(null)
    const [logRocketUserId, setLogRocketUserId] = useState(null)

    useEffect(() => {
        if (!customer?.email) {
            setBrazeUserId(`guest-${customer?.customerId}`)
            return
        }

        toSHA256(customer?.email).then(setBrazeUserId)
    }, [customer?.email, customer?.customerId])

    // LogRocket.identify() is append-only per session and cannot be undone, so only
    // registered shoppers with a real email get an identifier; guests stay anonymous.
    useEffect(() => {
        if (!customer?.isRegistered || !customer?.email) {
            setLogRocketUserId(null)
            return
        }

        toSHA256(customer.email).then(setLogRocketUserId)
    }, [customer?.isRegistered, customer?.email])

    /*-------- Extracted hooks --------*/
    const navigationHandlers = useNavigationHandlers({
        onDrawerClose: onClose,
        authModal,
        accountModalProps
    })

    const {trackPage} = useAppEffects({
        basket,
        customer,
        currency,
        location,
        isMaintenanceEnabled,
        brandId,
        onDrawerClose: onClose,
        setIsOnline,
        setSignifydReady,
        isDidomiReady,
        activeData,
        site,
        locale,
        updateBasket,
        updateCustomerForBasket
    })

    /*-------- Maintenance page --------*/
    const maintenancePageId = getSiteEnvVarValue(
        appConfig,
        'MAINTENANCE_GLOBAL_WEB_PAGE_ID',
        siteId
    )
    const {data: maintenancePageData, isLoading: isMaintenanceLoading} = useOptimizelyPage(
        isMaintenanceEnabled ? maintenancePageId : null
    )

    // Handle Didomi accept notice submission
    const handleDidomiConsentChanged = (cwtToken) => {
        // Check if the user has consented to Einstein (CQuotient) and update SFCC's DNT cookie accordingly.
        const isEienstienAuthorizedbyUser = Object.values(
            Didomi?.getCurrentUserStatus()?.vendors ?? {}
            ).some(
            vendor =>
                typeof vendor.id === 'string' &&
                vendor.id.includes('cquotient') &&
                vendor.enabled
            )
        
        updateDNT(!isEienstienAuthorizedbyUser)
    }

    if (isMaintenanceEnabled) {
        const maintenanceContent = maintenancePageData?.page || maintenancePageData
        const maintenanceHasContent = hasOptimizelyContent(maintenanceContent)
        return (
            <IntlProvider
                onError={(err) => {
                    if (!messages) {
                        return
                    }
                    if (err.code === 'MISSING_TRANSLATION') {
                        logger.warn('Missing translation', {
                            namespace: 'App.IntlProvider',
                            additionalProperties: {
                                errorMessage: err.message
                            }
                        })
                        return
                    }
                    throw err
                }}
                locale={targetLocale}
                messages={messages}
                defaultLocale={DEFAULT_LOCALE}
            >
                <Helmet>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <Box data-testid="maintenance-page" w={'100%'} h={'100vh'}>
                    {maintenanceHasContent ? (
                        <Optimizely
                            content={maintenanceContent}
                            isLoading={isMaintenanceLoading}
                            contentKey={maintenancePageId}
                        />
                    ) : (
                        <ContentFallback
                            contentKey={maintenancePageId}
                            note="Maintenance mode is enabled via site preferences — this page is served from the configured maintenance content."
                        />
                    )}
                </Box>
            </IntlProvider>
        )
    }

    const isBrazeReady = () =>
        !isServer &&
        isDidomiReady &&
        window?.Didomi &&
        (customer?.isGuest || (customer?.isRegistered && customer?.email))

    /*-------- Main render --------*/
    return (
        <Box className="sf-app" {...styles.container}>
            {IS_DIDOMI_ENABLED && (
                <DidomiSDK
                    apiKey={DIDOMI_PUBLIC_API_KEY}
                    iabVersion={2}
                    noticeId={DIDOMI_NOTICE_ID}
                    gdprAppliesGlobally={true}
                    embedTCFStub={true}
                    config={{
                        theme: {
                            font: `${isGarage ? 'GarageNeue' : 'AzoSans'}, sans-serif`
                        },
                        app: {privacyPolicyURL: `${appOrigin}${buildUrl('/privacy-policy.html')}`},
                        languages: {enabled: [DIDOMI_LANGUAGE], default: DIDOMI_LANGUAGE}
                    }}
                    onReady={() => setIsDidomiReady(true)}
                    onConsentChanged={handleDidomiConsentChanged}
                />
            )}
            {/* GTM loads after Didomi is ready (or immediately if Didomi is disabled) */}
            {isDidomiReady && <GTMScript />}
            {isBrazeReady() && <BrazeScript brazeUserId={brazeUserId} />}
            {getSiteEnvVarValue(appConfig, 'IS_LOGROCKET_ENABLED', siteId) === 'true' && isDidomiReady && (
                <LogRocketScript
                    userId={logRocketUserId}
                    traits={{
                        customerType: customer?.customerType,
                        isRegistered: !!customer?.isRegistered
                    }}
                />
            )}
            {getSiteEnvVarValue(appConfig, 'AB_TESTING_SCRIPT_SRC', siteId) && (
                <Script
                    src={getSiteEnvVarValue(appConfig, 'AB_TESTING_SCRIPT_SRC', siteId)}
                    delayUntilIdle
                />
            )}
            <AppProviders
                getTokenWhenReady={getTokenWhenReady}
                appConfig={appConfig}
                normalizedLang={normalizedLang}
                targetLocale={targetLocale}
                messages={messages}
                currency={currency}
                isCheckout={isCheckout}
                isSignifydReady={isSignifydReady}
                basket={basket}
                trackPage={trackPage}
            >
                <PendingWishlistReplay />
                <MainLayout
                    isCart={isCart}
                    isCheckout={isCheckout}
                    isCheckoutConfirmation={isCheckoutConfirmation}
                    isSsoCallback={isSsoCallback}
                    isOnline={isOnline}
                    styles={styles}
                    showCountrySelectorOnFooter={showCountrySelectorOnFooter}
                    handleShowCountrySelectorOnFooter={handleShowCountrySelectorOnFooter}
                    isDrawerOpen={isOpen}
                    onDrawerOpen={onOpen}
                    onDrawerClose={onClose}
                    categories={categories}
                    ROOT_CATEGORY={ROOT_CATEGORY}
                    authModal={authModal}
                    accountModalProps={accountModalProps}
                    {...navigationHandlers}
                >
                    {children}
                </MainLayout>
            </AppProviders>
            <PreviewModeProjectSelector />
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

const AppWrapper = (props) => (
    <AccountModalProvider>
        <App {...props} />
    </AccountModalProvider>
)

export default AppWrapper
